/**
 * UserRow.tsx — one user in the admin list (T-411, Slice 1).
 *
 * The state badge is the point of this row. "Invite expired" is not decoration: a
 * FORCE_CHANGE_PASSWORD user whose temp password lapsed CANNOT self-rescue — Cognito refuses
 * password reset in that state, and the web's enumeration-safe swallow means they would request
 * one and silently receive nothing. Resend is their only way in, so it is styled as the primary
 * action on exactly those rows and as a quiet link elsewhere.
 *
 * No disable/delete controls here — those are Slice 2, behind an IAM patch the task role does
 * not have yet. Rendering them now would produce AccessDenied at the worst moment.
 */
import type { AdminUserRow } from '@/lib/adminUsersApi'
import { stateLabel } from '@/lib/adminUsersApi'

const TONE_CLASS = {
  good: 'text-adj-positive',
  bad: 'text-adj-negative',
  neutral: 'text-muted-foreground',
} as const

interface UserRowProps {
  row: AdminUserRow
  busy: boolean
  onResend: (username: string) => void
  onToggleAdmin: (row: AdminUserRow) => void
}

export function UserRow({ row, busy, onResend, onToggleAdmin }: UserRowProps) {
  const { label, tone } = stateLabel(row)
  const isInvitee = row.state === 'invited' || row.state === 'invite_expired'
  const expired = row.state === 'invite_expired'
  const hintId = `resend-hint-${row.username}`

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border py-2 text-sm">
      <span className="min-w-0 flex-1 truncate text-foreground">{row.email ?? row.username}</span>

      <span className={`shrink-0 ${TONE_CLASS[tone]}`}>{label}</span>

      {/* The T-407 rBasis lesson: an expiry inferred from the account-creation date is a weaker
          claim than one measured from a recorded send, and the row must say which it is. */}
      {isInvitee && row.inviteClockBasis === 'account_created' ? (
        <span
          className="shrink-0 text-xs text-muted-foreground"
          title="No invite send was recorded for this user, so the date is inferred from when the account was created."
        >
          (approximate)
        </span>
      ) : null}

      {row.isAdmin ? (
        <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-xs text-foreground">
          Admin
        </span>
      ) : null}

      <div className="flex shrink-0 items-center gap-2">
        {isInvitee ? (
          <>
            <button
              type="button"
              aria-describedby={hintId}
              disabled={busy}
              onClick={() => onResend(row.username)}
              className={
                expired
                  ? 'rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground transition-colors hover:opacity-90 disabled:pointer-events-none disabled:opacity-50'
                  : 'rounded-md px-2 py-1 text-xs text-muted-foreground underline transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50'
              }
            >
              Resend invite
            </button>
            <span id={hintId} className="sr-only">
              Sends a new temporary password. Their previous temporary password will stop working.
            </span>
          </>
        ) : null}

        <button
          type="button"
          disabled={busy}
          onClick={() => onToggleAdmin(row)}
          className="rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          {row.isAdmin ? 'Remove admin' : 'Make admin'}
        </button>
      </div>
    </li>
  )
}
